import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import SearchIcon from "@mui/icons-material/Search";
import Item from "./Item";

import baseUrl from "../../../api/baseUrl";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import {
  posInputFocus,
  posSearch,
  setAllItems,
} from "../../../store/pos/posSlice";

interface ItemsProps {
  id: number;
  name: string;
  categoryId: number;
  categoryName: string;
  categoryShowInPos?: boolean;
  subCategoryId: number;
  subCategoryName: string;
  subCategoryShowInPos?: boolean;
  barcode: string;
  itemNumber?: string;
  salesCount?: number;
}

interface CategoryFilter {
  categoryId: number;
  name: string;
}

interface SubCategory {
  subCategoryId: number;
  categoryId: number;
  name: string;
}

const ALL_FILTER_ID = 0;

const formatAsUpperCase = (value?: string) => {
  if (!value) return "";
  return value.toUpperCase();
};

const chipBase =
  "inline-flex h-7 shrink-0 cursor-pointer items-center rounded-md border px-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] transition-colors select-none";

const chipIdle =
  "border-erp-line bg-[#f4f7fa] text-erp-muted hover:border-erp-accent hover:text-erp-accent";

const chipActive =
  "border-erp-navy bg-erp-navy text-white shadow-[0_2px_8px_rgba(11,28,44,0.2)]";

const chipSubActive =
  "border-erp-accent bg-erp-accent text-white shadow-[0_2px_8px_rgba(31,111,139,0.22)]";

const Items = forwardRef(({ barcode }: any, ref: any) => {
  const { search } = useSelector((state: RootState) => state.pos);
  const dispatch = useDispatch();

  const [originalData, setOriginalData] = useState<ItemsProps[]>([]);
  const [categories, setCategories] = useState<CategoryFilter[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([
    ALL_FILTER_ID,
  ]);
  const [selectedSubCategoryIds, setSelectedSubCategoryIds] = useState<
    number[]
  >([ALL_FILTER_ID]);

  const fetchData = useCallback(async () => {
    try {
      const res = await baseUrl.get("/branch/pos/find-items-with-zero");
      const items: ItemsProps[] = res.data.data || [];
      dispatch(setAllItems(items));

      setOriginalData(items);

      const groupedCategories: Record<number, CategoryFilter> = {};
      const groupedSubCategories: Record<number, SubCategory> = {};

      for (const item of items) {
        if (
          item.categoryId &&
          item.categoryShowInPos !== false &&
          !groupedCategories[item.categoryId]
        ) {
          groupedCategories[item.categoryId] = {
            categoryId: item.categoryId,
            name: item.categoryName,
          };
        }

        if (
          item.subCategoryId &&
          item.subCategoryShowInPos !== false &&
          !groupedSubCategories[item.subCategoryId]
        ) {
          groupedSubCategories[item.subCategoryId] = {
            subCategoryId: item.subCategoryId,
            categoryId: item.categoryId,
            name: item.subCategoryName,
          };
        }
      }

      setCategories(
        Object.values(groupedCategories).sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      );
      setSubCategories(Object.values(groupedSubCategories));
    } catch (error) {
      console.error("Failed to fetch items:", error);
    }
  }, [dispatch]);

  useImperativeHandle(ref, () => ({
    refreshItems: fetchData,
  }));

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const visibleSubCategories = useMemo(() => {
    if (selectedCategoryIds.includes(ALL_FILTER_ID)) {
      return subCategories.sort((a, b) => a.name.localeCompare(b.name));
    }

    return subCategories
      .filter((subCategory) =>
        selectedCategoryIds.includes(subCategory.categoryId),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [subCategories, selectedCategoryIds]);

  useEffect(() => {
    if (selectedSubCategoryIds.includes(ALL_FILTER_ID)) return;

    const visibleSubCategoryIds = visibleSubCategories.map(
      (subCategory) => subCategory.subCategoryId,
    );

    const nextSelectedSubCategories = selectedSubCategoryIds.filter((id) =>
      visibleSubCategoryIds.includes(id),
    );

    if (nextSelectedSubCategories.length === 0) {
      setSelectedSubCategoryIds([ALL_FILTER_ID]);
      return;
    }

    if (nextSelectedSubCategories.length !== selectedSubCategoryIds.length) {
      setSelectedSubCategoryIds(nextSelectedSubCategories);
    }
  }, [visibleSubCategories, selectedSubCategoryIds]);

  const filteredTableData = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return originalData
      .filter((item: any) => {
        const stockPass =
          item.modified === true || (item.stockQty ?? item.stock ?? 0) > 0;
        if (!stockPass) return false;

        const categoryPass =
          selectedCategoryIds.includes(ALL_FILTER_ID) ||
          selectedCategoryIds.includes(item.categoryId);
        if (!categoryPass) return false;

        const subCategoryPass =
          selectedSubCategoryIds.includes(ALL_FILTER_ID) ||
          selectedSubCategoryIds.includes(item.subCategoryId);
        if (!subCategoryPass) return false;

        if (!keyword) return true;

        return (
          item.name?.toLowerCase().includes(keyword) ||
          item.itemNumber?.toLowerCase().includes(keyword)
        );
      })
      .sort((a: any, b: any) => {
        const salesCountA = a.salesCount ?? 0;
        const salesCountB = b.salesCount ?? 0;
        if (salesCountB !== salesCountA) {
          return salesCountB - salesCountA;
        }

        const nameA = a.name?.toLowerCase() || "";
        const nameB = b.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      });
  }, [originalData, search, selectedCategoryIds, selectedSubCategoryIds]);

  const handleSearch = (e: any) => {
    const { name, value } = e.target;
    dispatch(posSearch({ name, value }));
  };

  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategoryIds((prev) => {
      if (categoryId === ALL_FILTER_ID) {
        return [ALL_FILTER_ID];
      }

      const withoutAll = prev.filter((id) => id !== ALL_FILTER_ID);
      const exists = withoutAll.includes(categoryId);
      const next = exists
        ? withoutAll.filter((id) => id !== categoryId)
        : [...withoutAll, categoryId];

      return next.length ? next : [ALL_FILTER_ID];
    });
  };

  const handleSubCategorySelect = (subCategoryId: number) => {
    setSelectedSubCategoryIds((prev) => {
      if (subCategoryId === ALL_FILTER_ID) {
        return [ALL_FILTER_ID];
      }

      const withoutAll = prev.filter((id) => id !== ALL_FILTER_ID);
      const exists = withoutAll.includes(subCategoryId);
      const next = exists
        ? withoutAll.filter((id) => id !== subCategoryId)
        : [...withoutAll, subCategoryId];

      return next.length ? next : [ALL_FILTER_ID];
    });
  };

  return (
    <div className="flex h-full w-full min-h-0 flex-col bg-white">
      <div className="shrink-0 border-b border-erp-line px-3 pb-2.5 pt-3">
        <div className="relative flex items-center">
          <SearchIcon className="pointer-events-none absolute left-3 !text-[18px] text-erp-muted" />
          <input
            type="search"
            onChange={handleSearch}
            value={search}
            name="search"
            ref={barcode}
            placeholder="Search items or scan… (Shift)"
            className="h-10 w-full rounded-lg border border-erp-line bg-[#f4f7fa] pl-10 pr-20 font-inter text-[13px] font-medium text-erp-ink outline-none transition-[border-color,box-shadow,background] placeholder:text-[#8ea3b8] focus:border-erp-accent focus:bg-white focus:shadow-[0_0_0_3px_rgba(31,111,139,0.14)]"
            onFocus={(e) => {
              dispatch(posInputFocus({ name: "common", value: e.target.name }));
            }}
          />
          <span className="pointer-events-none absolute right-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8ea3b8]">
            {filteredTableData.length}
          </span>
        </div>

        <div className="mt-2.5 flex flex-col gap-1.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="w-8 shrink-0 text-[10px] font-bold uppercase tracking-[0.1em] text-[#8ea3b8]">
              Cat
            </span>
            <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                type="button"
                className={`${chipBase} ${
                  selectedCategoryIds.includes(ALL_FILTER_ID)
                    ? chipActive
                    : chipIdle
                }`}
                onClick={() => handleCategorySelect(ALL_FILTER_ID)}
              >
                {formatAsUpperCase("all")}
              </button>
              {categories?.map((item: CategoryFilter) => (
                <button
                  type="button"
                  key={item.categoryId}
                  className={`${chipBase} ${
                    selectedCategoryIds.includes(item?.categoryId)
                      ? chipActive
                      : chipIdle
                  }`}
                  onClick={() => handleCategorySelect(item.categoryId)}
                >
                  {formatAsUpperCase(item?.name)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex min-w-0 items-center gap-2">
            <span className="w-8 shrink-0 text-[10px] font-bold uppercase tracking-[0.1em] text-[#8ea3b8]">
              Sub
            </span>
            <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                type="button"
                className={`${chipBase} ${
                  selectedSubCategoryIds.includes(ALL_FILTER_ID)
                    ? chipSubActive
                    : chipIdle
                }`}
                onClick={() => handleSubCategorySelect(ALL_FILTER_ID)}
              >
                {formatAsUpperCase("all")}
              </button>
              {visibleSubCategories?.map((item: SubCategory) => (
                <button
                  type="button"
                  key={item.subCategoryId}
                  className={`${chipBase} ${
                    selectedSubCategoryIds.includes(item?.subCategoryId)
                      ? chipSubActive
                      : chipIdle
                  }`}
                  onClick={() => handleSubCategorySelect(item.subCategoryId)}
                >
                  {formatAsUpperCase(item?.name)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2.5 [scrollbar-width:thin] [scrollbar-color:#c5d0db_transparent]">
        {filteredTableData.length === 0 ? (
          <div className="flex h-full min-h-[180px] flex-col items-center justify-center gap-1 text-center">
            <p className="m-0 text-[13px] font-semibold text-erp-ink">
              No items match
            </p>
            <p className="m-0 text-[12px] text-erp-muted">
              Clear search or change category filters
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {filteredTableData.map((item: any) => (
              <Item key={item?.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

Items.displayName = "Items";
export default React.memo(Items);
